import DOMPurify from 'dompurify';

const ALLOW_IFRAME = true;

function normalizeSmartQuotes(str) {
  return String(str || '')
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'");
}

export function escapeHtml(input) {
  const s = input == null ? '' : String(input);
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(s));
  return div.innerHTML;
}

export function decodeHtmlEntities(content) {
  if (!content) return '';
  try {
    if (typeof DOMParser === 'undefined') return content;
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(content), 'text/html');
    return (doc.body && doc.body.textContent) || String(content);
  } catch {
    return String(content);
  }
}

export function containsHtml(content) {
  if (!content) return false;
  const s = String(content).trim();
  if (!/[<>]/.test(s)) return false;
  try {
    if (typeof DOMParser === 'undefined') return /<[^>]+>/.test(s);
    const doc = new DOMParser().parseFromString(s, 'text/html');
    return Array.from(doc.body.childNodes).some(n => n.nodeType === 1);
  } catch {
    return /<[^>]+>/.test(s);
  }
}

const ALLOWED_STYLE_PROPS = new Set([
  'color',
  'background-color',
  'text-align',
  'font-size',
  'font-style',
  'font-weight',
  'text-decoration',
  'padding-left',
  'margin-left',
  'list-style-type',
]);

const isCssColor = v =>
  /^#[0-9a-f]{3,8}$/i.test(v) ||
  /^rgba?\s*\(/i.test(v) ||
  /^hsla?\s*\(/i.test(v) ||
  /^[a-z]+$/i.test(v);

const isTextAlign = v => /^(left|right|center|justify)$/.test(v);
const isFontStyle = v => /^(normal|italic|oblique)$/.test(v);
const isFontWeight = v => /^(normal|bold|bolder|lighter|[1-9]00)$/.test(v);
const isTextDecoration = v =>
  /^(none|underline|line-through|overline)(\s+(underline|line-through|overline))*$/.test(
    v
  );
const CSS_LENGTH_RE = /^(\d+(?:\.\d+)?)(px|rem|em|%)$/;
const isFontSize = v => CSS_LENGTH_RE.test(v);

// Used for padding-left / margin-left coming out of the Quill normalizer,
// which emits `level * 3em` indent offsets.  The editor allows up to 9
// indent levels, so cap `em` at 32 to leave headroom above the legitimate
// 27em ceiling.  Reject negatives so content can't be pushed off-screen.
const CSS_LENGTH_MAX = { px: 200, em: 32, rem: 32, '%': 100 };
const isCssLength = v => {
  const m = CSS_LENGTH_RE.exec(v);
  if (!m) return false;
  const num = parseFloat(m[1]);
  const unit = m[2];
  return num <= CSS_LENGTH_MAX[unit];
};

// Standard CSS list-style-type values that we accept on <ol>/<ul>.
// The Quill normalizer emits decimal / lower-alpha / lower-roman for
// nested <ol> and disc / circle / square for nested <ul>; the rest of
// this list covers the common spec values an authored canned response
// might carry through.  Anything else (e.g. raw URLs for `url(...)`,
// experimental counter-style names, or unknown identifiers) is dropped.
const isListStyleType = v =>
  /^(?:disc|circle|square|decimal|decimal-leading-zero|lower-roman|upper-roman|lower-greek|lower-alpha|lower-latin|upper-alpha|upper-latin|armenian|georgian|none)$/i.test(
    v
  );

const STYLE_VALIDATORS = {
  color: isCssColor,
  'background-color': isCssColor,
  'text-align': isTextAlign,
  'font-style': isFontStyle,
  'font-weight': isFontWeight,
  'text-decoration': isTextDecoration,
  'font-size': isFontSize,
  'padding-left': isCssLength,
  'margin-left': isCssLength,
  'list-style-type': isListStyleType,
};

export function isSafeLinkUrl(raw) {
  if (!raw) return false;
  const val = String(raw).trim();
  const low = val.toLowerCase();
  if (low.startsWith('http:') || low.startsWith('https:')) {
    return true;
  }
  if (val.startsWith('/') || val.startsWith('#') || val.startsWith('.')) {
    return true;
  }
  if (low.startsWith('mailto:') || low.startsWith('tel:')) {
    return true;
  }
  return false;
}

export function sanitizeHtml(raw) {
  if (!raw) return '';
  const content = normalizeSmartQuotes(raw);

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return escapeHtml(content);
  }

  const styleHook = (_node, data) => {
    // DOMPurify 3.x exposes the attribute value as `attrValue` (the
    // older `value` property no longer exists), so we read and write
    // through `attrValue` here.
    if (data.attrName !== 'style' || typeof data.attrValue !== 'string') return;
    const safe = [];
    data.attrValue.split(';').forEach(part => {
      const decl = part.trim();
      if (!decl) return;
      const i = decl.indexOf(':');
      if (i === -1) return;
      const prop = decl.slice(0, i).trim().toLowerCase();
      const val = decl.slice(i + 1).trim();
      if (!ALLOWED_STYLE_PROPS.has(prop)) return;
      const validate = STYLE_VALIDATORS[prop];
      if (!validate || validate(val)) {
        safe.push(`${prop}: ${val}`);
      }
    });
    data.attrValue = safe.join('; ');
    if (!data.attrValue) data.keepAttr = false;
  };

  const afterAttrsHook = node => {
    const tag = node.nodeName.toLowerCase();
    if (tag === 'a') {
      const a = node;
      if ((a.getAttribute('target') || '').toLowerCase() === '_blank') {
        const existing = (a.getAttribute('rel') || '')
          .split(/\s+/)
          .filter(Boolean)
          .map(s => s.toLowerCase());
        const needed = ['noopener', 'noreferrer', 'nofollow'];
        a.setAttribute(
          'rel',
          Array.from(new Set([...existing, ...needed])).join(' ')
        );
      }
    }
  };

  DOMPurify.addHook('uponSanitizeAttribute', styleHook);
  DOMPurify.addHook('afterSanitizeAttributes', afterAttrsHook);

  try {
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'span',
        'b',
        'strong',
        'i',
        'em',
        'u',
        's',
        'del',
        'code',
        'br',
        'sub',
        'sup',
        'a',
        'p',
        'div',
        'blockquote',
        'pre',
        'hr',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'ul',
        'ol',
        'li',
        'img',
        'figure',
        'figcaption',
        'table',
        'thead',
        'tbody',
        'tfoot',
        'tr',
        'th',
        'td',
        ...(ALLOW_IFRAME ? ['iframe'] : []),
      ],
      ALLOWED_ATTR: [
        'class',
        'style',
        'title',
        'href',
        'target',
        'rel',
        'src',
        'alt',
        'width',
        'height',
        'rowspan',
        'colspan',
        ...(ALLOW_IFRAME
          ? ['allow', 'allowfullscreen', 'frameborder', 'scrolling']
          : []),
        'data-*',
      ],
      ALLOW_STYLE_ATTR: true,
      ALLOW_DATA_ATTR: true,
      ALLOW_ARIA_ATTR: true,
      FORBID_TAGS: ['script', 'style'],
      FORBID_ATTR: [
        'onerror',
        'onload',
        'onclick',
        'onmouseover',
        'onfocus',
        'onmouseenter',
        'onmouseleave',
        'onmousewheel',
        'onwheel',
        'onanimationstart',
        'onanimationend',
      ],
      ADD_ATTR: ['rel', 'target'],
      RETURN_TRUSTED_TYPE: false,
    });
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute', styleHook);
    DOMPurify.removeHook('afterSanitizeAttributes', afterAttrsHook);
  }
}

export function stripHtmlToText(content) {
  if (!content) return '';
  const decoded = decodeHtmlEntities(content);
  try {
    if (typeof document !== 'undefined') {
      const div = document.createElement('div');
      div.innerHTML = decoded;
      return (
        div.textContent || div.innerText || decoded.replace(/<[^>]*>/g, '')
      );
    }
  } catch (e) {
    // Ignore error
  }
  return decoded.replace(/<[^>]*>/g, '');
}

export default {
  decodeHtmlEntities,
  containsHtml,
  sanitizeHtml,
  stripHtmlToText,
  escapeHtml,
};
